import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import StructuredRecipeDisplay from '../components/StructuredRecipeDisplay';
import api from '../api/api';
import '../styles/pages/FlyerPhotoPage.css';

/**
 * FlyerPhotoPage - 特売品写真→レシピ生成
 * メイン機能2: スーパーの特売品（チラシ・販売棚など）から食材を認識してレシピを提案
 */
const FlyerPhotoPage = () => {
  const { user, plan } = useAuth();
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // 写真アップロード
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // 認識結果（新フォーマット対応）
  const [recommendedItems, setRecommendedItems] = useState([]);
  const [otherItems, setOtherItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [recognitionError, setRecognitionError] = useState(false);
  const [showOtherItems, setShowOtherItems] = useState(false);

  // 偏り検出
  const [biasInfo, setBiasInfo] = useState(null);
  const [addedSuggestions, setAddedSuggestions] = useState([]);
  const [addedMessage, setAddedMessage] = useState(null); // 追加完了メッセージ

  // レシピ生成
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);

  // 使用状況
  const [fridgeUsage, setFridgeUsage] = useState(null);

  // 調味料設定
  const [userSeasonings, setUserSeasonings] = useState([]);

  const isPremium = plan === 'premium';
  const isStandard = plan === 'standard';
  const canUseFeature = isStandard || isPremium;

  // ページタイトル設定
  useEffect(() => {
    document.title = 'チラシレシピ | つむぎ';
  }, []);

  // 使用状況と調味料設定を取得
  useEffect(() => {
    if (canUseFeature) {
      fetchUsage();
      fetchSeasonings();
    }
  }, [canUseFeature]);

  const fetchUsage = async () => {
    try {
      const response = await api.get('/api/fridge/usage');
      if (response.data.success) {
        setFridgeUsage(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const fetchSeasonings = async () => {
    try {
      const response = await api.get('/api/seasonings');
      if (response.data.success) {
        setUserSeasonings(response.data.seasoningsList || []);
      }
    } catch (error) {
      console.error('Failed to fetch seasonings:', error);
    }
  };

  // 写真ファイル選択
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);

      // プレビュー画像作成
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // チラシを分析
  const handleAnalyzeFlyer = async () => {
    if (!imageFile) return;

    setIsAnalyzing(true);
    setRecognitionError(false);

    try {
      // 画像をBase64に変換
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result.split(',')[1]; // data:image/jpeg;base64, を除去

        try {
          const response = await api.post('/api/fridge/analyze', {
            imageBase64: base64Image,
            type: 'flyer'
          });

          if (response.data.success) {
            const data = response.data.data || {};
            const recommended = data.recommended || [];
            const other = data.other || [];
            const all = data.allItems || [];

            // 旧フォーマット対応（items配列がある場合）
            if (data.items && data.items.length > 0) {
              setAllItems(data.items);
              setRecommendedItems([]);
              setOtherItems([]);
              setSelectedIngredients([]);
            } else if (recommended.length === 0 && other.length === 0 && all.length === 0) {
              setRecognitionError(true);
            } else {
              setRecommendedItems(recommended);
              setOtherItems(other);
              setAllItems(all);
              // おすすめ食材を初期選択状態に
              const recommendedNames = recommended.map(item => item.name);
              setSelectedIngredients(recommendedNames);

              // 偏り情報を保存
              if (data.bias && data.bias.hasBias) {
                setBiasInfo(data.bias);
              }
            }

            // 使用状況を更新
            if (response.data.usage) {
              setFridgeUsage({
                ...fridgeUsage,
                usage: response.data.usage.count,
                remaining: response.data.usage.remaining
              });
            }
          }
        } catch (error) {
          console.error('Flyer analysis failed:', error);

          if (error.response?.status === 403) {
            alert('この機能はスタンダードプラン以上でご利用いただけます');
          } else if (error.response?.status === 429) {
            alert('今月は上限までご利用いただきました。\n来月またご利用いただけます。');
          } else {
            setRecognitionError(true);
          }
        } finally {
          setIsAnalyzing(false);
        }
      };

      reader.readAsDataURL(imageFile);
    } catch (error) {
      console.error('Error reading file:', error);
      setIsAnalyzing(false);
      setRecognitionError(true);
    }
  };

  // 食材を選択/解除
  const toggleIngredientSelection = (ingredientName) => {
    if (selectedIngredients.includes(ingredientName)) {
      setSelectedIngredients(selectedIngredients.filter(name => name !== ingredientName));
    } else {
      setSelectedIngredients([...selectedIngredients, ingredientName]);
    }
  };

  // 偏り提案から食材を追加
  const handleAddSuggestion = (item) => {
    if (!addedSuggestions.includes(item)) {
      setAddedSuggestions([...addedSuggestions, item]);
      setSelectedIngredients([...selectedIngredients, item]);
    }
  };

  // おまかせで全ての提案食材を追加
  const handleAutoBalance = () => {
    const newItems = biasInfo.suggestions.filter(s => !addedSuggestions.includes(s));
    setAddedSuggestions([...addedSuggestions, ...newItems]);
    setSelectedIngredients([...selectedIngredients, ...newItems]);
    setBiasInfo(null);
    // 追加完了メッセージを表示
    setAddedMessage(`${newItems.join('と')}を追加したよ`);
    setTimeout(() => setAddedMessage(null), 3000);
  };

  // 単品追加時もメッセージ表示
  const handleAddSuggestionWithFeedback = (item) => {
    handleAddSuggestion(item);
    setAddedMessage(`${item}を追加したよ`);
    setTimeout(() => setAddedMessage(null), 3000);
  };

  // レシピ保存状態
  const [recipeSaveMessage, setRecipeSaveMessage] = useState(null);

  // レシピ自動保存
  const saveGeneratedRecipe = async (recipeMarkdown) => {
    try {
      // ## 1. 料理名 または ## 料理名 のパターンに対応
      const titleMatch = recipeMarkdown.match(/##\s*\d*\.?\s*(.+)/);
      const title = titleMatch ? titleMatch[1].trim() : '特売品レシピ';

      const autoTags = ['特売品レシピ'];
      const lowerRecipe = recipeMarkdown.toLowerCase();
      if (lowerRecipe.includes('簡単') || lowerRecipe.includes('時短')) autoTags.push('簡単');
      if (lowerRecipe.includes('節約')) autoTags.push('節約');

      const response = await api.post('/api/recipes/save', {
        title,
        content: recipeMarkdown.trim(),
        sourceConsultationId: null,
        tags: autoTags
      });

      if (response.data.success) {
        setRecipeSaveMessage(response.data.isNew ? `「${title}」を保存しました` : '既に保存済みです');
        setTimeout(() => setRecipeSaveMessage(null), 4000);
      }
      console.log('✅ レシピを保存しました:', title);
    } catch (error) {
      console.error('レシピ保存エラー:', error);
      setRecipeSaveMessage('レシピの保存に失敗しました');
      setTimeout(() => setRecipeSaveMessage(null), 4000);
    }
  };

  // レシピ生成
  const handleGenerateRecipe = async () => {
    if (selectedIngredients.length === 0) {
      alert('食材を1つ以上選択してください');
      return;
    }

    setIsGenerating(true);

    try {
      const response = await api.post('/api/fridge/recipe', {
        ingredients: selectedIngredients,
        userSeasonings: userSeasonings
      });

      if (response.data.success) {
        // localStorageに保存（複数レシピ対応）
        localStorage.setItem('flyerRecipeData', JSON.stringify({
          recipes: response.data.recipes || [response.data.recipe],
          recipe: response.data.recipe, // 後方互換性
          ingredients: selectedIngredients
        }));
        // 結果ページに遷移
        navigate('/flyer-recipe-result');
      }
    } catch (error) {
      console.error('Recipe generation failed:', error);

      if (error.response?.status === 429) {
        alert('今月の使用回数上限に達しました');
      } else {
        alert('レシピの生成に失敗しました。もう一度お試しください。');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // リセット
  const handleReset = () => {
    setGeneratedRecipe(null);
    setSelectedIngredients([]);
    setRecommendedItems([]);
    setOtherItems([]);
    setAllItems([]);
    setImageFile(null);
    setImagePreview(null);
    setRecognitionError(false);
    setShowOtherItems(false);
    setBiasInfo(null);
    setAddedSuggestions([]);
  };

  // 認識結果があるかどうか
  const hasRecognitionResults = recommendedItems.length > 0 || otherItems.length > 0 || allItems.length > 0;

  // 「自分で選び直す」で表示する食材リスト（other + allItems から recommended を除外）
  const getSelectableItems = () => {
    const recommendedNames = new Set(recommendedItems.map(item => item.name));
    const combinedItems = [...otherItems];

    // allItemsからotherにもrecommendedにもないものを追加
    allItems.forEach(item => {
      if (!recommendedNames.has(item.name) && !combinedItems.find(i => i.name === item.name)) {
        combinedItems.push(item);
      }
    });

    return combinedItems;
  };

  return (
    <AppLayout>
      <PageTitle
        title="特売品でレシピ"
        subtitle="つむぎが特売品で節約レシピを提案します"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="flyer-photo-page max-w-[600px] mx-auto px-4 pb-8">
        {/* チラシ解析中のローディング画面 */}
        {isAnalyzing && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-white/95 backdrop-blur-sm flex items-center justify-center z-[9999]">
            <div className="bg-gradient-to-br from-mint/10 to-honey/10 border-2 border-mint/30 p-12 px-14 rounded-3xl text-center shadow-2xl max-w-md">
              {/* つむぎ画像 */}
              <div className="mb-4 flex justify-center">
                <img
                  src="/tsumugi.png"
                  alt="つむぎ"
                  className="flyer-photo-page__tsumugi-image animate-bounce"
                />
              </div>
              <div className="w-[60px] h-[60px] border-[5px] border-gray-200 border-t-mint border-r-honey rounded-full mx-auto mb-6 animate-spin"></div>
              <p className="text-xl font-semibold text-gray-800 mb-3 tracking-wide">
                チラシを見ているよ...
              </p>
              <p className="text-[0.95rem] text-gray-600 mb-6 leading-relaxed">
                特売品を探しています
              </p>
              <div className="flex justify-center gap-2">
                <span className="w-2.5 h-2.5 bg-mint rounded-full animate-bounce"></span>
                <span className="w-2.5 h-2.5 bg-honey rounded-full animate-bounce delay-75"></span>
                <span className="w-2.5 h-2.5 bg-peach rounded-full animate-bounce delay-150"></span>
              </div>
            </div>
          </div>
        )}

        {/* レシピ生成中のローディング画面 */}
        {isGenerating && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-white/95 backdrop-blur-sm flex items-center justify-center z-[9999]">
            <div className="bg-gradient-to-br from-mint/10 to-honey/10 border-2 border-mint/30 p-12 px-14 rounded-3xl text-center shadow-2xl max-w-md">
              {/* つむぎ画像 */}
              <div className="mb-4 flex justify-center">
                <img
                  src="/tsumugi.png"
                  alt="つむぎ"
                  className="flyer-photo-page__tsumugi-image animate-bounce"
                />
              </div>
              <div className="w-[60px] h-[60px] border-[5px] border-gray-200 border-t-mint border-r-honey rounded-full mx-auto mb-6 animate-spin"></div>
              <p className="text-xl font-semibold text-gray-800 mb-3 tracking-wide">
                つむぎがレシピを考えています...
              </p>
              <p className="text-[0.95rem] text-gray-600 mb-6 leading-relaxed">
                栄養バランスを考えながら、美味しい献立を作っています
              </p>
              <div className="flex justify-center gap-2">
                <span className="w-2.5 h-2.5 bg-mint rounded-full animate-bounce"></span>
                <span className="w-2.5 h-2.5 bg-honey rounded-full animate-bounce delay-75"></span>
                <span className="w-2.5 h-2.5 bg-peach rounded-full animate-bounce delay-150"></span>
              </div>
            </div>
          </div>
        )}

        {!canUseFeature ? (
          /* プラン外ユーザー */
          <div>
            <div className="tsumugi-card tsumugi-card--peach mb-4">
              <h3 className="tsumugi-card-title mb-4">🛒 特売品認識機能</h3>
              <p className="tsumugi-card-text mb-4">
                この機能はスタンダードプラン以上でご利用いただけます
              </p>

              <div className="mb-4 space-y-3">
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                  <div className="text-2xl shrink-0">📰</div>
                  <div>
                    <p className="font-semibold text-sm text-gray-800">新聞チラシを撮影</p>
                    <p className="text-xs text-gray-600">買い物前に献立を決められます</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                  <div className="text-2xl shrink-0">💰</div>
                  <div>
                    <p className="font-semibold text-sm text-gray-800">特売品で節約レシピ</p>
                    <p className="text-xs text-gray-600">お買い得な食材で献立を提案</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                  <div className="text-2xl shrink-0">🏥</div>
                  <div>
                    <p className="font-semibold text-sm text-gray-800">橋本病にも配慮</p>
                    <p className="text-xs text-gray-600">栄養バランスを考えた献立</p>
                  </div>
                </div>
              </div>

              <button
                className="tsumugi-btn tsumugi-btn-primary w-full"
                onClick={() => navigate('/plans')}
              >
                プランを見る
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 使い方ガイド */}
            {!generatedRecipe && !hasRecognitionResults && !imageFile && (
              <div className="tsumugi-card tsumugi-card--shell mb-4">
                <h3 className="tsumugi-card-title mb-4">📸 こんな写真が使えます</h3>

                <div className="mb-4 space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                    <div className="text-2xl shrink-0">📰</div>
                    <div>
                      <p className="font-semibold text-sm text-gray-800 mb-1">新聞のチラシ</p>
                      <p className="text-xs text-gray-600">買い物前に献立を決められます</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                    <div className="text-2xl shrink-0">🏪</div>
                    <div>
                      <p className="font-semibold text-sm text-gray-800 mb-1">店頭の特売コーナー</p>
                      <p className="text-xs text-gray-600">その場で何を買うか決められます</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                    <div className="text-2xl shrink-0">💻</div>
                    <div>
                      <p className="font-semibold text-sm text-gray-800 mb-1">ネットスーパーの画面</p>
                      <p className="text-xs text-gray-600">スクショして注文前に確認</p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">💡 撮影のコツ</p>
                  <ul className="text-xs text-gray-600 space-y-1 ml-4">
                    <li>• 明るい場所で撮影</li>
                    <li>• 商品名や価格が見えるように</li>
                    <li>• 全体が入るように撮影</li>
                  </ul>
                </div>
              </div>
            )}

            {/* 写真アップロード */}
            {!generatedRecipe && !hasRecognitionResults && (
              <div className="tsumugi-card tsumugi-card--mint mb-4">
                <h3 className="tsumugi-card-title">特売品を撮影・選択してください</h3>
                <p className="text-hint text-sm mb-3">
                  チラシでも、ネットスーパーのスクショでも大丈夫。AIが食材を認識します
                </p>

                {/* プレビュー表示 */}
                {imagePreview && (
                  <div className="flyer-photo-page__preview-area mb-4">
                    <img src={imagePreview} alt="プレビュー" className="w-full h-auto max-h-[300px] object-contain rounded-2xl border border-gray-200" />
                    <button
                      className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray w-full mt-2"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                    >
                      写真を変更する
                    </button>
                  </div>
                )}

                {/* アップロードボタン */}
                {!imagePreview && (
                  <div className="flyer-photo-page__upload-buttons space-y-3 mb-4">
                    {/* カメラ撮影 */}
                    <button
                      className="tsumugi-btn tsumugi-btn-primary w-full"
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      📸 カメラで撮影
                    </button>
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {/* ギャラリーから選択 */}
                    <button
                      className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      🖼️ ギャラリー・スクショから選択
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}

                {imageFile && (
                  <button
                    className="tsumugi-btn tsumugi-btn-primary w-full"
                    onClick={handleAnalyzeFlyer}
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? '分析中...' : '特売品を認識する'}
                  </button>
                )}
              </div>
            )}

            {/* 認識エラー時 */}
            {recognitionError && !generatedRecipe && (
              <div className="tsumugi-card tsumugi-card--peach mb-4">
                <div className="flex items-start gap-3 mb-4">
                  <img
                    src="/tsumugi.png"
                    alt="つむぎ"
                    className="w-12 h-12 object-contain shrink-0"
                  />
                  <div className="p-3 bg-white/80 rounded-2xl rounded-tl-sm">
                    <p className="text-sm text-gray-800 leading-relaxed">
                      うまく読み取れなかった...ごめんね。<br />
                      食材を直接教えてくれる？
                    </p>
                  </div>
                </div>
                <button
                  className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--peach w-full"
                  onClick={handleReset}
                >
                  もう一度撮影する
                </button>
              </div>
            )}

            {/* 認識結果（つむぎのおすすめUI） */}
            {hasRecognitionResults && !generatedRecipe && (
              <div className="tsumugi-card mb-4">
                {/* おすすめセクション（recommendedがある場合） */}
                {recommendedItems.length > 0 && (
                  <>
                    {/* ヘッダー */}
                    <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="text-xl">💡</span> つむぎのおすすめ
                    </h3>

                    {/* つむぎの吹き出しメッセージ */}
                    <div className="flex items-start gap-3 mb-4">
                      <img
                        src="/tsumugi.png"
                        alt="つむぎ"
                        className="w-12 h-12 object-contain shrink-0"
                      />
                      <div className="p-3 bg-gradient-to-br from-mint/20 to-honey/10 rounded-2xl rounded-tl-sm">
                        <p className="text-sm text-gray-800 leading-relaxed">
                          疲れにくい体づくりに良いものを<br />
                          選んでみたよ
                        </p>
                      </div>
                    </div>

                    {/* おすすめ食材リスト */}
                    <div className="space-y-2 mb-4">
                      {recommendedItems.map((item, index) => {
                        const isSelected = selectedIngredients.includes(item.name);
                        return (
                          <div
                            key={`recommended-${index}`}
                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? 'bg-gradient-to-r from-green-100 to-green-50 border-2 border-green-400'
                                : 'bg-white border-2 border-gray-200 hover:border-green-300'
                            }`}
                            onClick={() => toggleIngredientSelection(item.name)}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                              isSelected ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                            }`}>
                              {isSelected ? '✓' : ''}
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-800">{item.name}</div>
                              {item.reason && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {item.reason}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* 追加完了メッセージ */}
                    {addedMessage && (
                      <div className="bg-green-50 border-2 border-green-300 rounded-xl p-3 mb-4 flex items-center gap-2">
                        <span className="text-green-600 text-lg">✓</span>
                        <span className="text-green-700 font-medium">{addedMessage}</span>
                      </div>
                    )}

                    {/* 偏り検出時のUI */}
                    {biasInfo && biasInfo.hasBias && (
                      <div className="tsumugi-card tsumugi-card--peach mb-4">
                        <div className="flex items-start gap-3 mb-3">
                          <img src="/tsumugi.png" alt="つむぎ" className="w-12 h-12 object-contain" />
                          <div className="p-3 bg-white/80 rounded-2xl rounded-tl-sm">
                            <p className="text-sm text-gray-800 leading-relaxed">
                              {biasInfo.message}<br />
                              おうちにある食材、追加する？
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-3">
                          {biasInfo.suggestions.map((item) => (
                            <button
                              key={item}
                              onClick={() => handleAddSuggestionWithFeedback(item)}
                              disabled={addedSuggestions.includes(item)}
                              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                                addedSuggestions.includes(item)
                                  ? 'bg-green-100 text-green-700 border-2 border-green-400'
                                  : 'bg-mint/30 border-2 border-mint text-gray-800 hover:bg-mint/50'
                              }`}
                            >
                              {addedSuggestions.includes(item) ? '✓ ' : '+ '}{item}
                            </button>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => setBiasInfo(null)}
                            className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-xl text-sm font-medium"
                          >
                            このままでOK
                          </button>
                          <button
                            onClick={handleAutoBalance}
                            className="flex-1 py-2 px-4 bg-mint text-white rounded-xl text-sm font-semibold shadow-md"
                          >
                            おまかせで追加
                          </button>
                        </div>
                      </div>
                    )}

                    {/* メインボタン */}
                    <button
                      className="tsumugi-btn tsumugi-btn-primary w-full mb-3"
                      onClick={handleGenerateRecipe}
                      disabled={isGenerating || selectedIngredients.length === 0}
                    >
                      {isGenerating ? 'レシピ生成中...' : 'このままレシピを作る'}
                    </button>

                    {/* 「自分で選び直す」セクション */}
                    {getSelectableItems().length > 0 && (
                      <div className="border-t border-gray-200 pt-3">
                        <button
                          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                          onClick={() => setShowOtherItems(!showOtherItems)}
                        >
                          <span className={`transform transition-transform duration-200 ${showOtherItems ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                          自分で選び直す
                          <span className="text-xs text-gray-400">（他の食材も見る）</span>
                        </button>

                        {/* 展開時の他の食材リスト */}
                        {showOtherItems && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="grid grid-cols-3 gap-2">
                              {getSelectableItems().map((item, index) => {
                                const isSelected = selectedIngredients.includes(item.name);
                                return (
                                  <div
                                    key={`other-${index}`}
                                    className={`relative py-3 px-2 border-2 rounded-xl bg-white cursor-pointer transition-all duration-200 text-center ${
                                      isSelected
                                        ? 'border-green-400 bg-gradient-to-br from-green-100/80 to-green-50/60 shadow-sm'
                                        : 'border-gray-200 hover:border-green-300 hover:bg-green-50/30'
                                    }`}
                                    onClick={() => toggleIngredientSelection(item.name)}
                                  >
                                    <div className="text-sm font-medium text-gray-800">{item.name}</div>
                                    {isSelected && (
                                      <div className="flyer-photo-page__item-check absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-green-500 text-white rounded-full text-xs font-bold">✓</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* 旧フォーマット対応: recommendedがない場合は従来のUIを表示 */}
                {recommendedItems.length === 0 && allItems.length > 0 && (
                  <>
                    {/* つむぎのメッセージ */}
                    <div className="flex items-start gap-3 mb-4">
                      <img
                        src="/tsumugi.png"
                        alt="つむぎ"
                        className="w-12 h-12 object-contain shrink-0"
                      />
                      <div className="p-3 bg-gradient-to-br from-mint/20 to-honey/10 rounded-2xl rounded-tl-sm">
                        <p className="text-sm text-gray-800 leading-relaxed">
                          チラシ見てみたよ！<br />
                          いろいろお買い得みたいだね。<br />
                          今日使いたい食材を選んでね
                        </p>
                      </div>
                    </div>

                    {/* 全食材をシンプルにリスト表示 */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {allItems.map((item, index) => {
                        const isSelected = selectedIngredients.includes(item.name);
                        return (
                          <div
                            key={`all-${index}`}
                            className={`relative py-3 px-2 border-2 rounded-xl bg-white cursor-pointer transition-all duration-200 text-center ${
                              isSelected
                                ? 'border-green-400 bg-gradient-to-br from-green-100/80 to-green-50/60 shadow-sm'
                                : 'border-gray-200 hover:border-green-300 hover:bg-green-50/30'
                            }`}
                            onClick={() => toggleIngredientSelection(item.name)}
                          >
                            <div className="text-sm font-medium text-gray-800">{item.name}</div>
                            {isSelected && (
                              <div className="flyer-photo-page__item-check absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-green-500 text-white rounded-full text-xs font-bold">✓</div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="flyer-photo-page__selected-count text-center text-sm font-semibold text-gray-800 py-2 px-3 bg-gradient-to-br from-yellow-100/80 to-yellow-50/60 rounded-xl border-2 border-yellow-200/50 mb-4">
                      選択中: <span className="text-pink-500 font-bold">{selectedIngredients.length}</span>個
                    </div>

                    <button
                      className="tsumugi-btn tsumugi-btn-primary w-full"
                      onClick={handleGenerateRecipe}
                      disabled={isGenerating || selectedIngredients.length === 0}
                    >
                      {isGenerating ? 'レシピ生成中...' : '選んだ食材でレシピを提案してもらう'}
                    </button>
                  </>
                )}

                {/* 別のチラシを読み込むボタン */}
                <button
                  className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray w-full mt-3"
                  onClick={handleReset}
                >
                  別のチラシを読み込む
                </button>
              </div>
            )}

            {/* 生成されたレシピ */}
            {generatedRecipe && (
              <div className="tsumugi-card tsumugi-card--honey">
                <h3 className="tsumugi-card-title">つむぎのお得レシピ</h3>
                {/* レシピ保存メッセージ */}
                {recipeSaveMessage && (
                  <div className="bg-green-50 border-2 border-green-300 rounded-xl p-3 mb-4 flex items-center gap-2">
                    <span className="text-green-600 text-lg">💾</span>
                    <span className="text-green-700 font-medium">{recipeSaveMessage}</span>
                  </div>
                )}
                <div className="py-3 px-3 bg-gradient-to-br from-green-200/60 to-green-100/40 rounded-xl text-sm text-gray-800 mb-4 font-medium border border-green-300/30">
                  使用食材: {selectedIngredients.join('、')}
                </div>
                <StructuredRecipeDisplay recipeMarkdown={generatedRecipe} />

                <button
                  className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint w-full mt-4"
                  onClick={handleReset}
                >
                  別の特売品を読み込む
                </button>
              </div>
            )}
          </>
        )}

        {/* 注意書き */}
        <div className="flyer-photo-page__disclaimer mt-8 py-4 px-4 text-center">
          <p className="text-hint text-xs m-0 leading-relaxed">
            ※ このサービスは医療行為ではありません。一般的な栄養の知恵をもとにした献立提案です。
          </p>
        </div>
      </div>
    </AppLayout>
  );
};

export default FlyerPhotoPage;
